import React, { useEffect, useState } from 'react'
import { Card, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { getDuplicates, mergeDeals, type DuplicateReport, type Confidence } from './crmApi'

/**
 * Duplicate hints (Phase 2.10).
 *
 * The same company gets entered twice all the time: once from a show import, once when someone
 * starts a trial. Both records then accumulate half a history each, and neither tells the truth.
 *
 * Confidence here is EARNED, not assumed, and the three tiers are different kinds of claim:
 *
 *   CERTAIN   same WeQuote account id, or the same person's email. A fact.
 *   LIKELY    identical company name once Ltd/LLC/punctuation are removed. Two real companies
 *             can still share a name, so it is not certain.
 *   POSSIBLE  the names resemble each other. A hint. Go and look.
 *
 * Nothing is merged automatically. Merging is destructive-ish and hard to undo, and an
 * over-eager duplicate finder would happily fuse two real customers into one.
 */

const STYLE: Record<Confidence, { label: string; bg: string; fg: string }> = {
  certain: { label: 'Certain', bg: '#FEE2E2', fg: '#991B1B' },
  likely: { label: 'Likely', bg: '#FEF3C7', fg: '#92400E' },
  possible: { label: 'Possible', bg: '#E0F2FE', fg: '#075985' },
}

const PAGE_SIZE = 50

export const CrmDuplicates: React.FC<{ onMerged?: () => void }> = ({ onMerged }) => {
  const [report, setReport] = useState<DuplicateReport | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  const load = () => { getDuplicates().then(setReport).catch(() => setReport(null)) }
  useEffect(load, [])

  const merge = async (primaryId: string, secondaryId: string, name: string) => {
    if (!window.confirm(`Merge "${name}" into the lead above it?\n\nThe one above keeps its stage and primary contact; this one's contact becomes a secondary contact. This is not easily undone.`)) return
    setBusy(secondaryId)
    try {
      await mergeDeals(primaryId, secondaryId)
      crmToast('Merged', 'success')
      load()
      onMerged?.()
    } catch {
      crmToast('Could not merge', 'error')
    } finally {
      setBusy(null)
    }
  }

  if (!report || report.clusters.length === 0) return null
  const { counts } = report
  const pageCount = Math.max(1, Math.ceil(report.clusters.length / PAGE_SIZE))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageClusters = report.clusters.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE)
  const from = clampedPage * PAGE_SIZE + 1
  const to = Math.min(report.clusters.length, (clampedPage + 1) * PAGE_SIZE)

  return (
    <Card
      title="Possible duplicates"
      subtitle={`${counts.certain} certain · ${counts.likely} likely · ${counts.possible} possible`}
    >
      <p className="text-[11px] text-txt-tertiary leading-relaxed mb-3">{report.note}</p>

      <div className="space-y-3">
        {pageClusters.map((c, i) => {
          const s = STYLE[c.confidence]
          const [primary, ...others] = c.leads
          return (
            <div key={i} className="rounded-xl border border-border overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-1.5" style={{ background: s.bg }}>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: s.fg }}>{s.label}</span>
                <span className="text-[11px]" style={{ color: s.fg }}>{c.evidence}</span>
              </div>
              {c.leads.map((l, idx) => (
                <div key={l.dealId} className="flex items-center gap-3 px-3 py-2 border-t border-border">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-medium text-txt-primary truncate">
                      {l.company}
                      {idx === 0 && <span className="ml-1.5 text-[10px] text-txt-tertiary">(keeps its stage if you merge)</span>}
                    </p>
                    <p className="text-[11px] text-txt-tertiary truncate">
                      {l.stage.replace(/_/g, ' ')} · created {l.createdDays}d ago{l.email ? ` · ${l.email}` : ''}
                    </p>
                  </div>
                  {idx > 0 && (
                    <Button size="sm" variant="secondary" loading={busy === l.dealId} onClick={() => merge(primary.dealId, l.dealId, l.company)}>
                      Merge into first
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )
        })}
      </div>

      {report.clusters.length > PAGE_SIZE && (
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
          <p className="text-[11px] text-txt-tertiary">Showing {from}–{to} of {report.clusters.length} clusters</p>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={clampedPage === 0} className="px-2.5 py-1 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40">‹ Prev</button>
            <span className="px-1 text-xs font-medium text-txt-primary whitespace-nowrap">Page {clampedPage + 1} of {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={clampedPage >= pageCount - 1} className="px-2.5 py-1 text-xs font-medium rounded-lg text-txt-secondary hover:bg-surface-hover disabled:opacity-40">Next ›</button>
          </div>
        </div>
      )}
    </Card>
  )
}
